import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBuilding,
  FaChartLine,
  FaChevronRight,
  FaCoins,
  FaDoorOpen,
  FaHeadset,
  FaPhoneAlt,
  FaUserShield,
  FaWallet,
} from 'react-icons/fa';
import PlatformDashboard from '../pages/PlatformDashboard';
import TenantManagement from '../pages/TenantManagement';
import DemoLeads from '../pages/DemoLeads';
import PlatformCompanyBooks from '../pages/PlatformCompanyBooks';
import PlatformBilling from '../pages/PlatformBilling';
import PlatformSupportTickets from '../pages/PlatformSupportTickets';
import './PlatformLayout.css';

function getNavItems(isSuperAdmin) {
  const baseItems = [
    {
      to: '/platform/dashboard',
      label: 'Platform Overview',
      description: 'Growth, tenant activity, and system pulse',
      icon: FaChartLine,
    },
    {
      to: '/platform/tenants',
      label: 'Tenant Control',
      description: 'Onboarding, activation, and station setup',
      icon: FaBuilding,
    },
    {
      to: '/platform/demo-leads',
      label: 'Demo Pipeline',
      description: 'Requests, follow-ups, demos, and conversion',
      icon: FaPhoneAlt,
    },
    {
      to: '/platform/support-tickets',
      label: 'Support Cases',
      description: 'Customer issues, ticket tracking, and response flow',
      icon: FaHeadset,
    },
  ];

  if (!isSuperAdmin) return baseItems;

  return [
    ...baseItems,
    {
      to: '/platform/company-books',
      label: 'Company Books',
      description: 'Recurring and one-off FuelFlow company expenses',
      icon: FaCoins,
    },
    {
      to: '/platform/billing',
      label: 'Billing Control',
      description: 'Tenant subscriptions, UPI collection, and due enforcement',
      icon: FaWallet,
    },
  ];
}

function readLS(key, fallback = '') {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === undefined) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export default function PlatformLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const role = String(readLS('role', '')).toLowerCase();
  const name = String(readLS('name', 'Platform User'));
  const isPlatformUser = role === 'super_admin' || role === 'support_admin';
  const isSuperAdmin = role === 'super_admin';
  const navItems = useMemo(() => getNavItems(isSuperAdmin), [isSuperAdmin]);

  const currentModule = useMemo(() => {
    return (
      navItems.find((item) => location.pathname === item.to) ||
      navItems.find((item) => location.pathname.startsWith(item.to)) ||
      navItems[0]
    );
  }, [location.pathname]);

  useEffect(() => {
    if (!isPlatformUser) {
      navigate('/');
    }
  }, [isPlatformUser, navigate]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const setByViewport = () => setSidebarOpen(mq.matches);
    setByViewport();
    mq.addEventListener('change', setByViewport);
    return () => mq.removeEventListener('change', setByViewport);
  }, []);

  const logout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (!isPlatformUser) return <Navigate to="/" replace />;

  return (
    <div className={`platform-shell ${sidebarOpen ? 'with-sb' : 'no-sb'}`}>
      <div
        className={`platform-sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`platform-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="platform-brand-wrap">
          <div className="platform-brand-mark">FF</div>
          <div>
            <div className="platform-brand-title">FuelFlow Nexus</div>
            <div className="platform-brand-sub">Platform Command Center</div>
          </div>
          <button
            className="platform-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>

        <div className="platform-user-box">
          <div className="platform-user-avatar">{name?.charAt(0)?.toUpperCase() || 'F'}</div>
          <div>
            <div className="platform-user-name">{name}</div>
            <div className="platform-user-role">
              {role === 'super_admin' ? 'Super Admin' : 'Support Admin'}
            </div>
          </div>
        </div>

        <nav className="platform-menu">
          <div className="platform-menu-section-label">Platform Workspace</div>

          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} className="platform-menu-link">
                <span className="platform-menu-icon">
                  <Icon />
                </span>
                <span className="platform-menu-copy">
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
                <span className="platform-menu-arrow">
                  <FaChevronRight />
                </span>
              </NavLink>
            );
          })}
        </nav>

        <div className="platform-sidebar-bottom">
          <button className="platform-logout-btn" onClick={logout}>
            <FaDoorOpen />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="platform-main">
        <div className="platform-topbar">
          <button className="platform-hamburger" onClick={() => setSidebarOpen(true)}>
            ☰
          </button>

          <div className="platform-top-copy">
            <div className="platform-top-kicker">Platform Control</div>
            <div className="platform-top-title">{currentModule?.label || 'FuelFlow Platform'}</div>
            <div className="platform-top-sub">
              {currentModule?.description || 'Control tenants, leads, onboarding, and platform operations'}
            </div>
          </div>

          <div className="platform-top-profile">
            <div className="platform-top-profile-mark">
              <FaUserShield />
            </div>
            <div>
              <strong>{name}</strong>
              <span>{role === 'super_admin' ? 'Super Admin' : 'Support Admin'}</span>
            </div>
          </div>
        </div>

        <div className="platform-content">
          <Routes>
            <Route path="/" element={<PlatformDashboard />} />
            <Route path="dashboard" element={<PlatformDashboard />} />
            <Route path="tenants" element={<TenantManagement />} />
            <Route path="demo-leads" element={<DemoLeads />} />
            <Route path="support-tickets" element={<PlatformSupportTickets />} />
            <Route path="company-books" element={isSuperAdmin ? <PlatformCompanyBooks /> : <PlatformDashboard />} />
            <Route path="billing" element={isSuperAdmin ? <PlatformBilling /> : <PlatformDashboard />} />
            <Route path="*" element={<PlatformDashboard />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
